// SetupGuideCard — sits at the top of each Setup tab. Renders the Odia
// guidance for the currently-active tab + a horizontal status strip showing
// which of the four setup tabs are complete and which are pending.
//
// Eye-catching design: large gradient hero band, animated pulse on the
// "Complete" stat when the user hits 100%, plus a per-tab "ଯାହା ଦରକାର
// ଅଛି" callout in red when a tab is pending.
//
// Mobile-first: stacked layout under sm, two columns >= md. The status strip
// is horizontally scrollable on mobile so all four tabs stay visible.

import { useMemo } from "react";
import { CheckCircle2, Circle, Clock, ListChecks, ChevronRight, Sparkles, AlertCircle } from "lucide-react";
import {
  SETUP_TABS, TAB_LABELS, TAB_GUIDES, computeTabStatus, pendingReason,
} from "@/lib/setupGuide";

export default function SetupGuideCard({
  activeTab,
  profile,
  profiles, // legacy prop
  vendors,
  cchs,
  enrolls,
  onJumpTab,
  // iter123 — authoritative server status. When passed, drives completion
  // and per-tab pending reasons (matches the home dashboard's
  // IncompleteSetupBanner so the two screens never disagree).
  serverSections,
  serverMissingBySection,
}) {
  const status = useMemo(
    () => computeTabStatus({ profile, profiles, vendors, cchs, enrolls, serverSections }),
    [profile, profiles, vendors, cchs, enrolls, serverSections],
  );
  const guide = TAB_GUIDES[activeTab] || TAB_GUIDES.schools;
  const completedCount = SETUP_TABS.filter((t) => status[t]).length;
  const totalCount = SETUP_TABS.length;
  const pctComplete = Math.round((completedCount / totalCount) * 100);
  const isAllDone = pctComplete >= 100;
  const activeTabDone = !!status[activeTab];
  // Resolve the most-active profile object (for pendingReason).
  const resolvedProfile = profile || (profiles && profiles[0]) || null;
  const reason = activeTabDone
    ? ""
    : pendingReason(activeTab, { profile: resolvedProfile, vendors, cchs, serverMissingBySection });

  // iter123 — when admin has marked extra fields mandatory, surface a small
  // checklist of EVERY missing field across EVERY section so users can fix
  // them all without bouncing between tabs to find out what's wrong.
  const tabToServerKey = { schools: "schoolIdentity", vendors: "vendors", cchs: "cchs", enrollment: "enrollment" };
  const allMissingPerTab = SETUP_TABS.reduce((acc, t) => {
    const list = serverMissingBySection?.[tabToServerKey[t]] || [];
    if (list.length) acc[t] = list;
    return acc;
  }, {});
  const totalMissing = Object.values(allMissingPerTab).reduce((a, l) => a + l.length, 0);

  return (
    <section
      data-testid="setup-guide-card"
      className={`mb-5 overflow-hidden rounded-2xl border-2 shadow-md transition-shadow ${
        isAllDone
          ? "border-moss/40 bg-gradient-to-br from-moss/10 via-moss/5 to-sand-100"
          : "border-clay/40 bg-gradient-to-br from-clay/8 via-ochre/8 to-sand-100"
      }`}
    >
      {/* === Status strip (always visible, EYE-CATCHING) === */}
      <div
        className={`border-b-2 px-4 py-3 sm:px-5 sm:py-4 ${
          isAllDone ? "border-moss/30 bg-moss/15" : "border-clay/25 bg-white/80"
        }`}
      >
        <div className="flex items-center justify-between gap-2">
          <p className={`flex items-center gap-1.5 text-[0.7rem] font-semibold uppercase tracking-[0.16em] sm:text-xs ${isAllDone ? "text-moss-dark" : "text-clay-dark"}`}>
            {isAllDone ? <Sparkles className="h-3.5 w-3.5" /> : <ListChecks className="h-3.5 w-3.5" />}
            ସେଟଅପ୍ ସ୍ଥିତି · {completedCount} / {totalCount} ସମ୍ପୂର୍ଣ୍ଣ
          </p>
          <p
            className={`text-xl font-extrabold sm:text-2xl ${isAllDone ? "text-moss-dark" : "text-clay-dark"} ${isAllDone ? "animate-pulse" : ""}`}
            data-testid="setup-guide-pct"
          >
            {pctComplete}%
          </p>
        </div>
        {/* Progress bar */}
        <div className="mt-2 h-2 w-full overflow-hidden rounded-full bg-clay/15">
          <div
            className={`h-full transition-all duration-700 ${isAllDone ? "bg-gradient-to-r from-moss to-moss-dark" : "bg-gradient-to-r from-clay via-ochre to-moss"}`}
            style={{ width: `${pctComplete}%` }}
          />
        </div>
        {isAllDone && (
          <p className="mt-2 text-center text-xs font-bold text-moss-dark" data-testid="setup-100-complete">
            🎉 100% Profile Complete — ସମସ୍ତ Setup ସମ୍ପୂର୍ଣ୍ଣ!
          </p>
        )}

        {/* iter123 — show EVERY missing mandatory field across EVERY section,
            grouped by tab. Authored by the backend (`field_config`-aware) so
            this list matches the home dashboard exactly. */}
        {!isAllDone && totalMissing > 0 && (
          <details className="mt-3 rounded-xl border border-amber-300 bg-amber-50/80 p-3" data-testid="setup-missing-overview">
            <summary className="flex cursor-pointer items-center justify-between text-xs font-semibold text-amber-900">
              <span className="flex items-center gap-1.5">
                <AlertCircle className="h-3.5 w-3.5" />
                <span>ଭର୍ତ୍ତି କରିବାକୁ ବାକି: <b>{totalMissing}</b> field{totalMissing > 1 ? "s" : ""}</span>
              </span>
              <span className="text-[0.65rem] text-amber-700">Tap to expand ↓</span>
            </summary>
            <div className="mt-3 space-y-3 text-xs text-amber-950">
              {SETUP_TABS.filter((t) => allMissingPerTab[t]?.length).map((t) => (
                <div key={t} data-testid={`setup-missing-section-${t}`}>
                  <button
                    type="button"
                    onClick={() => onJumpTab && onJumpTab(t)}
                    className="font-bold text-amber-900 underline-offset-2 hover:underline"
                  >
                    {TAB_LABELS[t]?.od || TAB_LABELS[t]?.en || t} ({allMissingPerTab[t].length})
                  </button>
                  <ul className="mt-1 list-disc pl-5">
                    {allMissingPerTab[t].slice(0, 6).map((m, i) => (
                      <li key={i} className="leading-snug">{m}</li>
                    ))}
                    {allMissingPerTab[t].length > 6 && (
                      <li className="italic text-amber-700">… +{allMissingPerTab[t].length - 6} more</li>
                    )}
                  </ul>
                </div>
              ))}
            </div>
          </details>
        )}
        {/* Tab status pills (horizontally scrollable on mobile) */}
        <div className="mt-3 flex gap-1.5 overflow-x-auto pb-1" data-testid="setup-status-strip">
          {SETUP_TABS.map((t) => {
            const done = status[t];
            const isActive = t === activeTab;
            return (
              <button
                key={t}
                type="button"
                onClick={() => onJumpTab && onJumpTab(t)}
                data-testid={`setup-status-pill-${t}`}
                className={`flex shrink-0 items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-semibold transition-all ${
                  isActive
                    ? "border-clay bg-clay text-white shadow-sm"
                    : done
                    ? "border-moss/40 bg-moss/15 text-moss-dark hover:bg-moss/25"
                    : "border-amber-300 bg-amber-50 text-amber-900 hover:border-amber-400"
                }`}
              >
                {done ? (
                  <CheckCircle2 className="h-3.5 w-3.5" />
                ) : (
                  <Circle className="h-3.5 w-3.5" />
                )}
                <span>{TAB_LABELS[t]?.od || t}</span>
                {done && <span className="text-[0.6rem] opacity-80">✓</span>}
              </button>
            );
          })}
        </div>
      </div>

      {/* === Guide content === */}
      <div className="px-4 py-4 sm:px-5 sm:py-5">
        <div className="flex items-start gap-3">
          <span className={`hidden h-10 w-10 shrink-0 items-center justify-center rounded-full text-clay sm:inline-flex ${activeTabDone ? "bg-moss/15 text-moss-dark" : "bg-clay/15"}`}>
            <ListChecks className="h-5 w-5" />
          </span>
          <div className="flex-1 min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <h3
                className="font-heading text-base font-bold tracking-tight text-ink-900 sm:text-lg"
                data-testid="setup-guide-title"
              >
                {guide.title}
              </h3>
              {activeTabDone ? (
                <span className="inline-flex items-center gap-1 rounded-full bg-moss px-2 py-0.5 text-[0.62rem] font-semibold text-white" data-testid="setup-guide-status-done">
                  <CheckCircle2 className="h-3 w-3" /> Complete
                </span>
              ) : (
                <span className="inline-flex items-center gap-1 rounded-full bg-amber-500 px-2 py-0.5 text-[0.62rem] font-semibold text-white" data-testid="setup-guide-status-pending">
                  <Clock className="h-3 w-3" /> Pending
                </span>
              )}
              <span className="inline-flex items-center gap-1 rounded-full bg-ochre/15 px-2 py-0.5 text-[0.6rem] font-semibold text-clay-dark">
                ⏱ ~{guide.timeMin} min
              </span>
            </div>
            <p className="mt-1.5 text-xs text-ink-700 sm:text-sm" data-testid="setup-guide-why">
              {guide.why}
            </p>
            <ul className="mt-3 space-y-1.5 text-xs text-ink-900 sm:text-sm" data-testid="setup-guide-steps">
              {guide.steps.map((s, i) => (
                <li key={i} className="flex items-start gap-2">
                  <ChevronRight className="mt-0.5 h-3.5 w-3.5 shrink-0 text-clay" />
                  <span dangerouslySetInnerHTML={{ __html: s }} />
                </li>
              ))}
            </ul>

            {/* Pending-reason callout */}
            {!activeTabDone && reason && (
              <div
                className="mt-4 flex items-start gap-2 rounded-xl border-2 border-red-300 bg-red-50 p-3 text-xs text-red-900 sm:text-sm"
                data-testid="setup-guide-pending-reason"
              >
                <AlertCircle className="mt-0.5 h-4 w-4 shrink-0 animate-pulse" />
                <p>
                  <b>⚠️ ଦୟାକରି ଲକ୍ଷ୍ୟ କରନ୍ତୁ:</b> {reason}
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
